import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { layout, space } from 'styled-system';

import analytics from '@src/analytics';
import analyticsEvents from '@src/analytics/events';
import { interactionProperties } from '@src/analytics/properties';

const StyledButtonMinimal = styled.button`
  ${props => (props.$inline ? '' : 'width: 100%;')}
  transition: border-color 200ms ease-in-out, background-color 200ms ease-in-out;
  box-sizing: border-box;
  background: transparent;
  border-radius: 100px;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: inline-block;
  flex-shrink: 0;
  font-family: ${props => props.theme.font.primary};
  font-feature-settings: 'ss03' on;
  font-weight: 500;
  letter-spacing: 0.04em;
  opacity: ${props => (props.disabled ? 0.3 : 1)};
  outline: none;
  text-decoration: none;
  white-space: nowrap;

  span+svg,
  svg+span {
    margin-left: 8px;
  }

  svg {
    vertical-align: middle;
    
    path {
      fill: white;
    }
  }

  ${props => {
    if (props.$buttonSize === 'xs') {
      return `
        font-size: 12px;
        height: 24px;
        line-height: 16px;
        padding: 4px 16px;
      `;
    }
    if (props.$buttonSize === 's') {
      return `
        font-size: 14px;
        height: 32px;
        line-height: 16px;
        padding: 8px 24px;
      `;
    }
    if (props.$buttonSize === 'm') {
      return `
        font-size: 16px;
        height: 40px;
        line-height: 24px;
        padding: 8px 24px;
      `;
    }
    if (props.$buttonSize === 'l') {
      return `
        font-size: 16px;
        height: 48px;
        line-height: 24px;
        padding: 12px 24px;
      `;
    }
  }}

  ${space}
  ${layout}
`;

const StyledButtonTertiary = styled(StyledButtonMinimal)`
  color: ${props => props.theme.color[props.color]};

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg path {
    fill: ${props => props.theme.color[props.color]};
  }
`;

const StyledButtonPrimary = styled(StyledButtonMinimal)`
  background-color: ${({ color, theme }) => theme.color[color]};
  color: white;

  &:hover,
  &:active {
    background-color: ${({ color, theme }) => theme.color[`${color}Hover`]};
  }
`;

const StyledButtonSecondary = styled(StyledButtonMinimal)`
  background-color: transparent;
  box-shadow: inset 0 0 0 2px ${props => props.theme.color[props.color]};
  color: ${props => props.theme.color[props.color]};

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const StyledButtonIcon = styled(StyledButtonMinimal)`
  background-color: ${({ color, theme }) => theme.color[color]};
  height: 48px;
  padding: 12px;
`;

const ButtonVariant = Object.freeze({
  icon: StyledButtonIcon,
  primary: StyledButtonPrimary,
  secondary: StyledButtonSecondary,
  tertiary: StyledButtonTertiary,
});

export default function Button({
  analyticsName,
  children,
  inline,
  loading,
  onClick,
  refProp,
  size,
  style,
  variant,
  type,
  ...rest
}) {
  const StyledButton = ButtonVariant[variant];

  const onClickWithTracking = e => {
    const buttonText = typeof children === 'string' ? children : 'N/A';
    analytics.track(analyticsEvents.interaction.BUTTON_CLICKED, {
      [interactionProperties.BUTTON_NAME]: analyticsName || buttonText,
      [interactionProperties.BUTTON_TEXT]: buttonText,
    });
    if (typeof onClick === 'function') {
      onClick(e);
    }
  };

  return (
    <StyledButton
      onClick={onClickWithTracking}
      {...rest}
      $buttonSize={size}
      $inline={inline}
      ref={refProp}
      style={style}
      type={type}
    >
      {children}
      {loading}
    </StyledButton>
  );
}

Button.propTypes = {
  analyticsName: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['black', 'blue', 'green', 'red', 'grey']),
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 's', 'm', 'l']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'icon']),
};

Button.defaultProps = {
  color: 'black',
  inline: true,
  size: 'm',
  type: 'button',
  variant: 'primary',
};
